import { ProductValidationError } from '@lib/product/types'
import {
  type SanityFrameBuilderProductConfiguration,
  type SanityFrameBuilderType,
} from '@frame-builder/data/sanity/queries/types'
import { type ShopifyCreateFrameBuilderProductPayload } from './request'
import { type FrameBuilderProduct } from './types'
import {
  getIsProductPassepartoutSizeValid,
  getIsProductProfileAvailableForPassepartoutColor,
  getIsProductProfileAvailableForSpacerSize,
  getIsProductProfileAvailableForTexture,
  getIsProductProfileSizeValid,
  getIsProductPassepartoutColorValid,
} from './validation/product-validation'

export const shopifyFrameBuilderProductType = 'Generated Frame'

export const shopifyFrameBuilderPassepartoutProductType =
  'Generated Passepartout'

export const shopifyFrameBuilderProductTypeMap: Record<
  SanityFrameBuilderType,
  string
> = {
  frame: shopifyFrameBuilderProductType,
  passepartout: shopifyFrameBuilderPassepartoutProductType,
}

/**
 * Gets moulding, texture, mount color, glass, spacer size & color from payload.
 */
const getProductParts = (
  payload: ShopifyCreateFrameBuilderProductPayload,
  frameBuilderConfiguration: SanityFrameBuilderProductConfiguration,
) => {
  const profile = frameBuilderConfiguration.profiles.find((profile) => {
    return profile._id === payload.profileId
  })
  const texture = frameBuilderConfiguration.textures.find((texture) => {
    return texture._id === payload.textureId
  })
  const passepartoutColor = frameBuilderConfiguration.passepartoutColors.find(
    (passepartoutColor) => {
      return passepartoutColor._id === payload.passepartout.colorId
    },
  )
  const glass = frameBuilderConfiguration.glass.find((glass) => {
    return glass._id === payload.glassId
  })
  const spacerSize = frameBuilderConfiguration.spacerSizes.find(
    (spacerSize) => {
      return spacerSize._id === payload.spacerSizeId
    },
  )
  const spacerColor = frameBuilderConfiguration.spacerColors.find(
    (spacerColor) => {
      return spacerColor._id === payload.spacerColorId
    },
  )

  return [
    profile,
    texture,
    passepartoutColor,
    glass,
    spacerSize,
    spacerColor,
  ] as const
}

/**
 * Validates frame builder product payload data.
 */
export const validateFrameBuilderProductPayload = (
  payload: ShopifyCreateFrameBuilderProductPayload,
  frameBuilderConfiguration: SanityFrameBuilderProductConfiguration,
) => {
  const [profile, texture, passepartoutColor, glass, spacerSize, spacerColor] =
    getProductParts(payload, frameBuilderConfiguration)

  if (payload.frameBuilderType === 'frame') {
    // Validate moulding
    if (typeof profile === 'undefined') {
      throw new ProductValidationError('Profile is missing.')
    }

    const isProductProfileSizeValid = getIsProductProfileSizeValid(
      profile,
      payload.artwork.width,
      payload.artwork.height,
      passepartoutColor,
      payload.passepartout.width,
      payload.passepartout.height,
    )

    if (!isProductProfileSizeValid) {
      throw new ProductValidationError('Frame size is invalid for moulding.')
    }

    // Validate moulding & mount color availability
    if (typeof passepartoutColor !== 'undefined') {
      const isProductProfileAvailableForPassepartoutColor =
        getIsProductProfileAvailableForPassepartoutColor(
          profile,
          passepartoutColor,
        )

      if (!isProductProfileAvailableForPassepartoutColor) {
        throw new ProductValidationError('Mount color is invalid for moulding.')
      }
    }

    // Validate moulding & spacer size availability
    if (typeof spacerSize !== 'undefined' && spacerSize.thickness > 0) {
      const isProductProfileAvailableForSpacerSize =
        getIsProductProfileAvailableForSpacerSize(profile, spacerSize)

      if (!isProductProfileAvailableForSpacerSize) {
        throw new ProductValidationError('Spacer size is invalid.')
      }
    }

    // Validate texture
    if (typeof texture === 'undefined') {
      throw new ProductValidationError('Texture is missing.')
    }

    const isProductProfileAvailableForTexture =
      getIsProductProfileAvailableForTexture(
        profile,
        texture,
        frameBuilderConfiguration.profileTextures,
      )

    if (!isProductProfileAvailableForTexture) {
      throw new ProductValidationError(
        'Frame moulding and texture are incompatible.',
      )
    }

    // Validate glass
    if (typeof glass === 'undefined') {
      throw new ProductValidationError('Glass is missing.')
    }

    // Validate spacer color
    if (
      typeof spacerSize !== 'undefined' &&
      spacerSize.thickness > 0 &&
      typeof spacerColor === 'undefined'
    ) {
      throw new ProductValidationError('Spacer color is missing.')
    }
  }

  // Validate mount
  const isProductPassepartoutColorValid = getIsProductPassepartoutColorValid(
    payload.frameBuilderType,
    passepartoutColor,
  )

  if (!isProductPassepartoutColorValid) {
    throw new ProductValidationError('Mount color is invalid.')
  }

  const isProductPassepartoutSizeValid = getIsProductPassepartoutSizeValid(
    payload.artwork.width,
    payload.artwork.height,
    passepartoutColor,
    payload.passepartout.width,
    payload.passepartout.height,
  )

  if (!isProductPassepartoutSizeValid) {
    throw new ProductValidationError('Mount size is invalid.')
  }
}

/**
 * Gets frame builder product data from payload.
 */
export const getFrameBuilderProductFromPayload = (
  payload: ShopifyCreateFrameBuilderProductPayload,
  frameBuilderConfiguration: SanityFrameBuilderProductConfiguration,
) => {
  const [profile, texture, passepartoutColor, glass, spacerSize, spacerColor] =
    getProductParts(payload, frameBuilderConfiguration)

  const product: FrameBuilderProduct = {
    artwork: {
      width: payload.artwork.width,
      height: payload.artwork.height,
    },
    profile,
    texture,
    passepartout: {
      color: passepartoutColor,
      width: payload.passepartout.width,
      height: payload.passepartout.height,
    },
    glass,
    spacerSize,
    spacerColor,
  }

  return product
}
